import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUp, ArrowUpRight, Brain, Compass, Crosshair, Layers, Sunrise, LogOut, NotebookPen, TrendingUp, Trash2 } from "lucide-react";
import { EllieChat } from "@/components/companion/EllieChat";
import { Logo } from "@/components/layout/Logo";
import { Eyebrow } from "@/components/fionova/Primitives";
import { DeleteAccountDialog } from "@/components/fionova/DeleteAccountDialog";
import { openBillingPortal, getMemory } from "@/services/unsaidService";
import { unsaidStore } from "@/lib/unsaidStore";
import { EllieOrb } from "@/components/discovery/EllieOrb";
import { fmtDate } from "@/components/billing/TrialTerms";

const BillingStatus = ({ user }) => {
  const b = user?.billing || {};
  const st = user?.subscription_status;
  let text = null;
  if (b.cancel_at_period_end && (b.cancel_at || b.current_period_end)) text = `Your subscription is canceled. Access continues until ${fmtDate(b.cancel_at || b.current_period_end)}, and you won't be charged again.`;
  else if (user?.partner?.until && !st) text = `Complimentary invited access through ${fmtDate(user.partner.until)}. No payment details are on file.`;
  else if (st === "trialing" && b.trial_end && b.plan_offer === "intro_299") text = `Your first 30 days end ${fmtDate(b.trial_end)}. Your first $16.99 renewal is on that date unless you cancel before then.`;
  else if (st === "trialing" && b.trial_end) text = `Free trial ends ${fmtDate(b.trial_end)}. Your first $9.99 charge is on that date unless you cancel before then.`;
  else if (st === "past_due") text = "We couldn't process your last payment. Please update your payment method to keep your access.";
  else if (st === "active" && b.current_period_end) text = `Next $${(b.plan_amount || 9.99).toFixed(2)} charge on ${fmtDate(b.current_period_end)}.`;
  return text ? <p data-testid="home-billing-status" className="max-w-sm text-center text-xs leading-relaxed text-muted-foreground">{text}</p> : null;
};
import { useAuth } from "@/context/AuthContext";

const TOOLS = [
  { to: "/history/plan", label: "Daily Battle Plan", desc: "Your personalized roadmap for today. Focus on what matters, take meaningful action, and build momentum one step at a time.", Icon: Crosshair, area: "battle_plan" },
  { to: "/progress", label: "Your Progress", desc: "See how far you've come. Celebrate your wins, recognize patterns, and discover how you're growing over time.", Icon: TrendingUp },
  { to: "/history/xray", label: "Emotional X-Ray", desc: "Look beneath the surface of your emotions. Explore recurring patterns and gain a deeper understanding of yourself.", Icon: Layers },
  { to: "/future-self", label: "Future Self", desc: "Connect with the person you're becoming. Explore your aspirations and the steps that can bring your future closer.", Icon: Sunrise, area: "future_self" },
  { to: "/history/purpose", label: "Purpose Engine", desc: "Discover what truly drives you. Explore your values, motivations, and the things that give your life meaning.", Icon: Compass, area: "purpose" },
  { to: "/journal", label: "Daily Journal", desc: "Your private space to reflect. Capture your thoughts, celebrate meaningful moments, and recognize the lessons each day brings.", Icon: NotebookPen },
];

const Lit = ({ i }) => (
  <span data-testid={`home-tool-${i}-lit`} className="flex w-fit items-center gap-1.5 rounded-full bg-teal-400/15 px-2 py-0.5 text-[10.5px] font-semibold text-teal-600">
    <span className="h-1.5 w-1.5 rounded-full bg-teal-500 fio-pulse-dot" /> Ellie noticed this for you
  </span>
);

const ToolCard = ({ t, i, lit, onOpen }) => {
  const featured = i === 0;
  return (
    <motion.button data-testid={`home-tool-${i}`} onClick={onOpen}
      initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: 0.1 + i * 0.05 }}
      className={`group relative flex overflow-hidden rounded-[26px] p-5 text-left transition-[transform,box-shadow] duration-300 hover:-translate-y-0.5 active:scale-[0.99] ${featured
        ? "flex-col gap-3 bg-slate-900 text-white shadow-[0_24px_60px_-24px_rgba(13,148,136,0.6)] sm:col-span-2 sm:p-6"
        : "flex-col gap-2.5 bg-white/80 ring-1 ring-slate-200/80 backdrop-blur hover:shadow-[0_18px_40px_-22px_rgba(13,148,136,0.45)] hover:ring-teal-300/70"}`}>
      {featured && <><span aria-hidden className="pointer-events-none absolute -right-12 -top-16 h-44 w-44 rounded-full bg-teal-400/30 blur-3xl" /><span aria-hidden className="pointer-events-none absolute -bottom-16 left-10 h-36 w-36 rounded-full bg-violet-400/25 blur-3xl" /></>}
      <div className="relative flex w-full items-center justify-between">
        <span className={`flex h-11 w-11 items-center justify-center rounded-2xl ${featured ? "bg-white/10 text-teal-200 ring-1 ring-white/20" : "bg-gradient-to-br from-teal-50 to-violet-50 text-teal-700 ring-1 ring-teal-100"}`}>
          <t.Icon className="h-5 w-5" strokeWidth={1.6} />
        </span>
        <ArrowUpRight className={`h-5 w-5 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5 ${featured ? "text-teal-200" : "text-slate-400 group-hover:text-teal-600"}`} />
      </div>
      {featured && <span className="relative font-mono text-[10.5px] uppercase tracking-[0.18em] text-teal-200">Start here today</span>}
      <span className={`relative font-semibold tracking-tight ${featured ? "text-xl" : "text-[17px] text-slate-900"}`}>{t.label}</span>
      {t.area && lit.includes(t.area) && <span className="relative"><Lit i={i} /></span>}
      <span className={`relative text-sm leading-relaxed ${featured ? "text-slate-300" : "text-slate-600"}`}>{t.desc}</span>
    </motion.button>
  );
};

export default function SubscriberHome() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [params, setParams] = useSearchParams();
  const [billingBusy, setBillingBusy] = useState(false);
  const [billingErr, setBillingErr] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const firstName = (user?.name || "").split(" ")[0];

  const manageBilling = async () => {
    if (billingBusy) return;
    setBillingBusy(true); setBillingErr(false);
    try {
      const { portal_url } = await openBillingPortal();
      window.location.href = portal_url;
    } catch {
      setBillingBusy(false); setBillingErr(true);
    }
  };

  // Cross-device: seed premium tools (Future Self, Plan, Purpose) with the saved onboarding context.
  const [lit, setLit] = useState([]);
  useEffect(() => {
    const hasLocal = (unsaidStore.getOutcome()?.transcript || []).length > 0;
    getMemory().then((mem) => {
      setLit((mem?.discoveries || []).map((d) => d.area));
      if (hasLocal) return;
      if (mem?.unsaid_handoff?.transcript?.length) unsaidStore.saveOutcome(mem.unsaid_handoff);
      if (mem?.xray && !unsaidStore.getXrayOutcome()) unsaidStore.saveXrayOutcome({ xray: mem.xray });
    }).catch(() => {});
  }, []);

  if (params.get("chat") === "1") {
    return <EllieChat onBack={() => setParams({}, { replace: false })} />;
  }

  return (
    <div data-testid="page-home" className="fio-immersive min-h-screen w-full"><div className="fio-ambient" aria-hidden><span /><i /></div>
      <div className="mx-auto flex min-h-screen w-full max-w-[720px] flex-col px-5 py-8 sm:px-8">
        <div className="fio-appbar sticky top-3 z-20 flex items-center justify-between rounded-full py-1.5 pl-2 pr-4" data-testid="app-header">
          <Logo size={40} />
          <button data-testid="home-logout" onClick={() => { logout(); navigate("/"); }}
            className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground">
            <LogOut className="h-4 w-4" /> Sign out
          </button>
        </div>

        <div className="mt-8">
          <Eyebrow>{firstName ? `Welcome back, ${firstName}` : "Welcome back"}</Eyebrow>
          <h1 className="mt-2 text-3xl font-semibold leading-tight tracking-tight text-foreground sm:text-4xl">
            Ellie remembers where you left off.
          </h1>
        </div>

        <motion.button data-testid="home-continue-ellie" onClick={() => setParams({ chat: "1" })}
          initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
          className="mt-8 flex items-center justify-between rounded-[28px] bg-primary p-5 text-left text-primary-foreground shadow-[0_18px_40px_-18px_rgba(13,148,136,0.7)] transition-all hover:-translate-y-0.5">
          <EllieOrb size={52} testId="home-ellie-avatar" />
          <div className="ml-4 flex-1">
            <p className="text-lg font-semibold">Continue with Ellie</p>
            <p className="mt-1 text-sm leading-snug text-primary-foreground/85">Pick up where you left off. Ellie remembers your journey and is ready to explore what's next.</p>
          </div>
          <span className="flex h-12 w-12 items-center justify-center rounded-full bg-white/15">
            <ArrowUp className="h-6 w-6 rotate-45" />
          </span>
        </motion.button>

        <div className="mt-12" data-testid="home-toolkit">
          <h2 className="text-2xl font-semibold tracking-tight text-foreground">Your Personal Growth Toolkit</h2>
          <p className="mt-1.5 text-[15px] leading-relaxed text-muted-foreground">Everything you need to understand yourself, build momentum, and keep moving forward.</p>
        </div>
        <div className="mt-5 grid grid-cols-1 gap-3 sm:grid-cols-2 sm:gap-4">
          {TOOLS.map((t, i) => <ToolCard key={t.to} t={t} i={i} lit={lit} onOpen={() => navigate(t.to)} />)}
        </div>

        <button data-testid="home-memory-link" onClick={() => navigate("/memory")}
          className="fio-glass mt-3 flex items-center gap-3 rounded-3xl p-4 text-left transition-transform hover:-translate-y-0.5">
          <span className="flex h-10 w-10 items-center justify-center rounded-full bg-violet-500/10 text-violet-600"><Brain className="h-5 w-5" strokeWidth={1.5} /></span>
          <span className="flex-1">
            <span className="block text-base font-medium text-foreground">What Ellie remembers</span>
            <span className="block text-sm leading-snug text-muted-foreground">Review, correct or delete memories, and choose what Ellie can use.</span>
          </span>
        </button>

        <div className="mt-8 flex flex-col items-center gap-2.5">
          <BillingStatus user={user} />
          {user?.subscription_status && (
          <button data-testid="home-manage-billing" onClick={manageBilling} disabled={billingBusy}
            className="text-sm font-medium text-muted-foreground underline-offset-4 transition-colors hover:text-foreground hover:underline disabled:opacity-50">
            {billingBusy ? "Opening secure billing…" : "Manage Subscription & Billing"}
          </button>
          )}
          {billingErr && (
            <p data-testid="home-billing-error" className="text-xs text-muted-foreground/80">
              Couldn't open billing just now. Please try again in a moment.
            </p>
          )}
          <p className="text-center text-xs text-muted-foreground/70">You never have to start over.</p>
        </div>

        <div className="mt-10 border-t border-border/60 pt-6">
          <p className="eyebrow text-[11px] text-muted-foreground/80">Account</p>
          <div className="mt-3 rounded-3xl border border-destructive/25 bg-destructive/[0.04] p-5">
            <p className="text-sm font-medium text-foreground">Delete My Account &amp; Data</p>
            <p className="mt-1 text-sm leading-snug text-muted-foreground">
              Permanently delete your FioNova account and associated personal content. This action cannot be undone.
            </p>
            <button
              data-testid="home-delete-account"
              onClick={() => setDeleteOpen(true)}
              className="mt-3 inline-flex items-center gap-1.5 rounded-full border border-destructive/40 px-4 py-2 text-sm font-medium text-destructive transition-colors hover:bg-destructive/10">
              <Trash2 className="h-4 w-4" strokeWidth={1.75} /> Delete My Account &amp; Data
            </button>
          </div>
        </div>
      </div>
      <DeleteAccountDialog open={deleteOpen} onOpenChange={setDeleteOpen} />
    </div>
  );
}
